// ============================================================================
// PAY PERIOD STATS
// ============================================================================

import { DashboardShiftRequestDto, RateInfo } from '@umss/shared/data-access';
import { ShiftRequestStatusEnum } from '@umss/shared/utils';
export { DashboardShiftRequestDto } from '@umss/shared/data-access';

/**
 * Pay period statistics for the dashboard
 * Matches backend ContractorPayPeriodStatsDto
 */
export interface PayPeriodStatsDto {
  hoursWorked: number;
  estimatedGrossPay: number;
  hoursScheduled: number;
  periodStartDate: string; // YYYY-MM-DD format
  periodEndDate: string; // YYYY-MM-DD format
  totalMilesDriven: number;
  periodTrend: 'up' | 'down' | 'same';
}

// ============================================================================
// URGENT SHIFT
// ============================================================================

/**
 * Urgent shift alert for the dashboard
 * Derived from ShiftNeedDto with isUrgent=true
 */
export interface UrgentShiftDto {
  id: string;
  facilityId: string;
  facilityName: string;
  facilityLocation?: {
    id: number;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    stateCode: string;
    stateName: string;
    zipCode: string;
    countryCode: string;
    latitude?: number;
    longitude?: number;
    fullAddress: string;
  };
  facilityFullAddress?: string;
  distanceMiles?: number;
  facilitySubtypeId?: number;
  facilitySubtypeName?: string;
  scopeOfPractice?: string;
  scopeOfPracticeDisplay?: string;
  shiftDate: string; // YYYY-MM-DD format from backend DateOnly
  startTime: string; // HH:mm:ss format from backend TimeOnly
  endTime: string;
  hourlyRate?: number;
  percentageIncrease?: number; // Calculated: (urgentRate - baseRate) / baseRate * 100
  specialInstructions?: string;
  expiresAt?: string; // ISO datetime when the urgent shift expires
  rateInfo?: RateInfo;
}

// ============================================================================
// UPCOMING SHIFT (from ShiftDto)
// ============================================================================

/**
 * Upcoming shift for the dashboard
 * Matches backend ShiftDto structure
 */
export interface UpcomingShiftDto {
  id: string;
  facilityId: string;
  facilityName: string;
  facilityFullAddress?: string;
  facilitySubtypeId?: number;
  facilitySubtypeName?: string;
  distanceMiles?: number;
  contractorId: string;
  contractorName: string;
  scopeOfPractice: number;
  scopeOfPracticeDisplay: string;
  shiftType: number;
  shiftTypeDisplay: string;
  shiftDate: string; // YYYY-MM-DD format
  startTime: string; // HH:mm:ss format
  endTime: string;
  hourlyRate: number;
  specialInstructions?: string;
  status: ShiftRequestStatusEnum;
  statusDisplay: string;
  confirmedAt?: string;
  confirmedByName?: string;
  estimatedHours: number;
  estimatedPayment: number;
}

// ============================================================================
// SHIFT REQUEST (for pending requests on dashboard)
// ============================================================================
// DashboardShiftRequestDto is re-exported from @umss/shared/data-access above

// ============================================================================
// AGGREGATED DASHBOARD DATA
// ============================================================================

/**
 * Aggregated dashboard data returned from forkJoin
 * Contains all data needed for the dashboard view
 */
export interface DashboardDataDto {
  upcomingShifts: UpcomingShiftDto[];
  shiftRequests: DashboardShiftRequestDto[];
  payPeriodStats: PayPeriodStatsDto;
  urgentShifts: UrgentShiftDto[];
  contractorApplications: DashboardShiftRequestDto[];
}
